import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { CvMtaModel, MtaChangeType, DriverDetails } from '@acorn-portal/shared/src/services/api';
import { getInitialMTAStartDate } from '@acorn-portal/shared/src/utils';
import { formatISO } from 'date-fns';

export interface MtaDrivers extends DriverDetails {
	isNewDriver: boolean;
}

export interface IMidTermAdjustmentState extends CvMtaModel {
	isVehicleEligible: boolean;
	hasInitialData: boolean;
	policyReference: string;
	policyToken: string;
	mtaStartDate: string;
	isMtaFutureDated: boolean;
	changeType: MtaChangeType[];
	mtaPurchased?: boolean;
	changeOfAddress: boolean;
	changeOfVehicle: boolean;
	changeOfDriver: boolean;
	changeOfUsage: boolean;
}

export const INITIAL_STATE: IMidTermAdjustmentState = {
	isVehicleEligible: false,
	hasInitialData: false,
	policyReference: '',
	policyToken: '',
	mtaStartDate: getInitialMTAStartDate(formatISO(new Date())),
	changeType: [],
	invalidMtaStartDate: false,
	transactionId: '',
	vehicleDetails: {
		businessRadius: '',
		hasInternalRacking: false,
		vehicleCarriesDangerousGoods: false,
		vehicleVisitsHazardousSites: false,
		abiCode: '',
		abiGroup: 0,
		annualMileage: 0,
		businessMileage: 0,
		bodyType: '',
		registration: '',
		make: '',
		model: '',
		numberOfDoors: '',
		fuelType: '',
		transmission: '',
		grossWeight: undefined,
		engineSize: 0,
		value: 0,
		yearOfManufacture: 0,
		isLeftHandDrive: false,
		datePurchased: '',
		isQPlate: false,
		isImported: false,
		vehicleUse: '',
		ownership: '',
		registeredKeeper: '',
		registrationDate: '',
		countryOfRegistration: '',
		numberOfSeats: '',
		vehicleOvernight: '',
		overnightAddress: undefined,
		modifications: [],
		securityDevices: undefined,
		coverDefinitionCode: undefined,
		telematicsBox: false,
		licencingAuthority: '',
	},
	policyDetails: {
		policyholder: {
			isHomeOwner: false,
			address: {
				postcode: '',
				line1: '',
				city: '',
			},
		},
		operatingArea: '',
	},
	drivers: [],
	mtaPurchased: false,
	isMtaFutureDated: false,
	changeOfAddress: false,
	changeOfVehicle: false,
	changeOfDriver: false,
	changeOfUsage: false,
};

export const midTermAdjustmentSlice = createSlice({
	name: 'midTermAdjustment',
	initialState: INITIAL_STATE,
	reducers: {
		clearMidTermAdjustmentDetails: () => ({
			...INITIAL_STATE,
			mtaStartDate: getInitialMTAStartDate(formatISO(new Date())),
		}),
		updateMidTermAdjustmentDetails: (state, action: PayloadAction<Partial<IMidTermAdjustmentState>>) => {
			const newState: IMidTermAdjustmentState = {
				...state,
				...action.payload,
				vehicleDetails: {
					...state.vehicleDetails,
					...action.payload.vehicleDetails,
					overnightAddress: action.payload.vehicleDetails?.overnightAddress || {
						line1: action.payload.policyDetails?.policyholder.address.line1 ?? '',
						city: action.payload.policyDetails?.policyholder.address?.city ?? '',
						postcode: action.payload.policyDetails?.policyholder.address?.postcode ?? '',
					},
				},
				hasInitialData: true,
			};
			return newState;
		},
		setInitialDataFlag: (state, action: PayloadAction<boolean>) => {
			state.hasInitialData = action.payload;
		},
		setPolicyReference: (state, action: PayloadAction<string>) => {
			state.policyReference = action.payload;
		},
		setPolicyToken: (state, action: PayloadAction<string>) => {
			state.policyToken = action.payload;
		},
		setStartDate: (state, action: PayloadAction<string>) => {
			state.mtaStartDate = action.payload;
		},
		setMtaPurchased: (state, action: PayloadAction<boolean>) => {
			state.mtaPurchased = action.payload;
		},
		setVehicleIsEligible: (state, action: PayloadAction<boolean>) => {
			state.isVehicleEligible = action.payload;
		},
		setIsMtaFutureDate: (state, action: PayloadAction<boolean>) => {
			state.isMtaFutureDated = action.payload;
		},
		updateChangeOfAddress: (state, action: PayloadAction<boolean>) => {
			state.changeOfAddress = action.payload;
		},
		updateChangeOfVehicle: (state, action: PayloadAction<boolean>) => {
			state.changeOfVehicle = action.payload;
		},
		updateChangeOfDriver: (state, action: PayloadAction<boolean>) => {
			state.changeOfDriver = action.payload;
		},
		updateChangeOfUsage: (state, action: PayloadAction<boolean>) => {
			state.changeOfUsage = action.payload;
		},
	},
});

export const {
	clearMidTermAdjustmentDetails,
	updateMidTermAdjustmentDetails,
	setPolicyReference,
	setPolicyToken,
	setStartDate,
	setMtaPurchased,
	setInitialDataFlag,
	setVehicleIsEligible,
	setIsMtaFutureDate,
	updateChangeOfAddress,
	updateChangeOfVehicle,
	updateChangeOfDriver,
	updateChangeOfUsage,
} = midTermAdjustmentSlice.actions;

export default midTermAdjustmentSlice.reducer;
